import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Spin, message } from 'antd';
import TaskDetails from '../../components/Tasks/TaskDetails';
import { fetchTaskById, updateTask, fetchTaskHistory } from '../../redux/tasksSlice';
import { fetchUsers } from '../../redux/userOrganisationsSlice';
import { canViewTask } from '../../utils/taskPermissions';

const TaskDetailsPage = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { current: task, isLoading: taskLoading } = useSelector((state) => state.tasks);
    const { list: users, isLoading: usersLoading } = useSelector((state) => state.userOrganisations);
    const { list: candidates, isLoading: candidatesLoading } = useSelector((state) => state.candidates);
    const { orgId, user, role } = useSelector((state) => state.auth);

    const userId = user?.id ? parseInt(user.id) : null;

    const loading = taskLoading || usersLoading || candidatesLoading;

    useEffect(() => {
        if (!id) return;
        const load = async () => {
            try {
                await Promise.all([
                    dispatch(fetchTaskById(id)).unwrap(),
                    dispatch(fetchTaskHistory({ taskId: id })).unwrap(),
                    users?.length ? Promise.resolve() : dispatch(fetchUsers()).unwrap(),
                ]);
            } catch (e) {
                message.error('Не удалось загрузить задачу');
                navigate('/tasks');
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, dispatch, orgId]);

    // Проверка прав доступа к задаче
    useEffect(() => {
        if (!task || !userId || !role) return;

        const { canView, reason } = canViewTask(task, userId, role);

        if (!canView) {
            message.error(reason || 'У вас нет прав для просмотра этой задачи');
            navigate('/tasks');
        }
    }, [task, userId, role, navigate]);

    const handleClose = useCallback(() => {
        navigate('/tasks');
    }, [navigate]);

    const handleCommentSubmit = useCallback(async (taskId, comment) => {
        try {
            await dispatch(updateTask({ id: taskId, task: { comment } })).unwrap();
        } catch (e) {
            message.error('Ошибка при сохранении комментария');
        }
    }, [dispatch]);

    const handleUpdate = useCallback(async (taskId, formData) => {
        try {
            await dispatch(updateTask({ id: taskId, task: formData })).unwrap();
            message.success('Задача обновлена');
            await dispatch(fetchTaskById(taskId)).unwrap();
        } catch (e) {
            message.error('Ошибка при обновлении задачи');
        }
    }, [dispatch]);

    const handleFetchTaskHistory = useCallback(async (taskId) => {
        await dispatch(fetchTaskHistory({ taskId: taskId })).unwrap();
    }, [dispatch]);

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!task) return null;

    return (
        <div style={{ padding: 24 }}>
            <TaskDetails
                visible={true}
                onClose={handleClose}
                task={task}
                onCommentSubmit={handleCommentSubmit}
                users={users}
                candidates={candidates}
                onEditTask={handleUpdate}
                fetchTaskHistory={handleFetchTaskHistory}
            />
        </div>
    );
};

export default TaskDetailsPage;